import { useState, useEffect, useRef } from "react";
import {
  UserPlus, Plus, Trash2, RefreshCw, CheckCircle2, Clock,
  XCircle, Loader2, Search, ShieldCheck,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { adminFetch } from "./adminUtils";

const STATUS_CONFIG = {
  pending:  { label: "Pendiente",  icon: Clock,        color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  accepted: { label: "Aceptada",   icon: CheckCircle2, color: "text-green-400 bg-green-500/10 border-green-500/20" },
  expired:  { label: "Expirada",   icon: XCircle,      color: "text-muted-foreground bg-slate-700/30 border-slate-700" },
  revoked:  { label: "Revocada",   icon: XCircle,      color: "text-red-400 bg-red-500/10 border-red-500/20" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

export default function AdminSuperadmins() {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [search, setSearch] = useState("");
  const fetchedRef = useRef(false);

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const data = await adminFetch("/api/admin/superadmin-invitations");
      setInvitations(data.invitations || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fetchedRef.current) { fetchedRef.current = true; loadInvitations(); }
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await adminFetch("/api/admin/superadmin-invitations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || null }),
      });
      toast.success(`Invitacion de admin enviada a ${email}`);
      setEmail(""); setName("");
      loadInvitations();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (id) => {
    setRevoking(id);
    try {
      await adminFetch(`/api/admin/superadmin-invitations/${id}`, { method: "DELETE" });
      toast.success("Invitacion revocada");
      loadInvitations();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(null);
    }
  };

  const filtered = invitations.filter((inv) => {
    const q = search.toLowerCase();
    return !q || inv.email?.toLowerCase().includes(q) || inv.name?.toLowerCase().includes(q);
  });

  const counts = invitations.reduce((acc, inv) => {
    acc[inv.status] = (acc[inv.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8" data-testid="superadmin-invitations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500/15 rounded-xl flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Administradores</h1>
            <p className="text-muted-foreground text-sm">Invitar y gestionar cuentas de superadmin</p>
          </div>
        </div>
        <Button onClick={loadInvitations} variant="outline" size="sm"
          data-testid="refresh-superadmin-invitations-btn"
          className="border-slate-700 text-slate-400 hover:text-white">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualizar
        </Button>
      </div>

      {/* Stats pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <span key={key}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${cfg.color}`}>
            {cfg.label}: {counts[key] || 0}
          </span>
        ))}
      </div>

      {/* Send form */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6">
        <h2 className="text-white text-sm font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-orange-400" />
          Invitar nuevo administrador
        </h2>
        <form onSubmit={handleSend} className="flex flex-wrap items-end gap-3" data-testid="superadmin-invite-form">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-slate-400 text-xs mb-1.5">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ejemplo.com"
              data-testid="superadmin-invite-email"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
              required
            />
          </div>
          <div className="w-48">
            <Label className="text-slate-400 text-xs mb-1.5">Nombre (opcional)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre"
              data-testid="superadmin-invite-name"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600"
            />
          </div>
          <Button type="submit" disabled={sending || !email.trim()}
            data-testid="superadmin-invite-submit"
            className="bg-orange-500 hover:bg-orange-600 text-white">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
            Invitar Admin
          </Button>
        </form>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por email o nombre..."
          data-testid="superadmin-search"
          className="pl-10 bg-slate-900 border-slate-800 text-white placeholder:text-slate-600"
        />
      </div>

      {/* List */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="w-8 h-8 text-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">
              {search ? "Sin resultados" : "No hay invitaciones de admin aun"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filtered.map((inv) => (
              <div key={inv.invitation_id} data-testid={`sa-inv-${inv.invitation_id}`}
                className="px-5 py-4 flex items-center gap-4 hover:bg-slate-800/30 transition-colors">
                <div className="w-9 h-9 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4 text-orange-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-slate-200 text-sm font-medium truncate">
                      {inv.name || inv.email}
                    </p>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="text-muted-foreground text-xs truncate">{inv.email}</p>
                  <p className="text-muted-foreground text-[10px] mt-0.5">
                    Invitado: {new Date(inv.created_at).toLocaleDateString("es-ES")}
                    {inv.invited_by_email && ` por ${inv.invited_by_email}`}
                  </p>
                </div>
                {inv.status === "pending" && (
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleRevoke(inv.invitation_id)}
                    disabled={revoking === inv.invitation_id}
                    data-testid={`revoke-sa-${inv.invitation_id}`}
                    className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                  >
                    {revoking === inv.invitation_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />
                    }
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
